import { inject, injectable } from "inversify";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import type { IOrganizationStore } from "../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../services/IOrganizationStore";
import type { UserGroupConfigType } from "@whatslly/types/UserGroups";
import { AssignUsersToGroupResponse, CreateGroupResponse, GetUserGroupsResponse, RemoveGroupResponse } from "@whatslly/types/UserGroups";
import { deleteJSON, getJSON, postJSON, putJSON } from "@whatslly/commons/apiUtils";
import { modalService } from "@whatslly/ui-components/modals";
import { ConfirmModal } from "@whatslly/ui-components/modals/ConfirmModal";
import i18n from "../../i18n/config";
import { UserPermissions } from "@whatslly/commons/granularPermissions";
import { hasPermission } from "@whatslly/commons/platformPermissions";
import { parseOrReturnError } from "@whatslly/commons/parseOrReturnError";
import type { UserSupportedLanguageType } from "@whatslly/types/User";
import { UserChannels } from "@whatslly/types/User";
import { AddGroupModal } from "./AddGroupModal";
import type { NavigateFunction } from "react-router";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { MONGO_ERRORS } from "@whatslly/commons/mongo-errors";
import { LICENSE_ERRORS } from "@whatslly/commons/license-errors";
import type { TableSort } from "@whatslly/ui-components/v2/Tables/Table/TableTypes";
import { UrlStore } from "../../services/urlStore";
import debounce from "lodash/debounce";
import { OrgConfigsStore } from "../../services/OrgConfigsStore";

export type GroupMember = {
  _id: string;
  name: string;
  loading?: boolean;
  channels?: UserChannels;
  username?: string;
  lastLogin?: string;
  loginProvider: string;
  userId: string | undefined;
  language?: UserSupportedLanguageType;
  active?: boolean;
  groupIds?: string[];
};
export type UserGroup = {
  _id: string;
  permissions: string;
  name: string;
  external?: string;
  loading?: boolean;
  users: GroupMember[];
  configs: UserGroupConfigType;
};

type UserGroupWithOptionalUsers = Omit<UserGroup, "users"> & { users?: GroupMember[] };

@injectable()
export class UserGroupsStore {
  private _groups: { true?: UserGroup[]; false?: Omit<UserGroup, "users">[] } = {};
  private pendingOperation: Promise<any> | undefined;
  constructor(
    @inject(InjectUserStore) private readonly userStore: IUserStore,
    @inject(InjectOrganizationStore) private readonly orgStore: IOrganizationStore,
    @inject(UrlStore) private readonly urlStore: UrlStore,
    @inject(OrgConfigsStore) private readonly orgConfigsStore: OrgConfigsStore,
    @inject(FrontLogger) private readonly logger: FrontLogger
  ) {
    makeObservable<UserGroupsStore, "_groups">(this, {
      _groups: observable,
      groups: computed,
      groupsList: computed({ keepAlive: true }),
      canUpdateGroup: computed,
      casAssignGroup: computed,
      createGroup: action.bound,
      reset: action.bound,
      sort: computed,
      setSort: action.bound,
      showDefaultAutoSyncConfig: computed,
      channelConfigs: computed,
    });
    reaction(
      () => this.tableFilters,
      debounce(() => this.reset(), 250)
    );
  }

  reset() {
    this._groups = {};
  }

  get canUpdateGroup() {
    return hasPermission(this.userStore.user, UserPermissions["user_management.update_groups"]);
  }

  get casAssignGroup() {
    return hasPermission(this.userStore.user, UserPermissions["user_management.assign_groups"]);
  }

  private async _getGroups<T extends boolean>(withUsers: T): Promise<T extends true ? UserGroup[] : Omit<UserGroup, "users">[]> {
    if (this._groups[`${withUsers}`]) return this._groups[`${withUsers}`] as any;
    const orgId = await this.orgStore.orgId;

    const queryParams = new URLSearchParams();
    const { sort, sortBy } = this.tableFilters;
    if (withUsers) queryParams.set("withUsers", "true");
    if (sort) queryParams.set("sort", sort);
    if (sortBy) queryParams.set("sortBy", sortBy);

    const res = await getJSON({
      urlSuffix: `/integrations/user-groups/${orgId}?${queryParams.toString()}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
    }).then((res) => res.json() as GetUserGroupsResponse);
    if (!res.success) {
      throw new Error(res.error);
    }
    const groups = res.groups.map((g) =>
      Object.assign(g, {
        users: g.users?.map(({ name, _id, loginProvider, username, channels, lastLogin, userId, groupIds }) => ({
          name,
          _id,
          loginProvider,
          username,
          channels,
          lastLogin,
          userId,
          groupIds,
        })),
      })
    ) as UserGroup[];
    runInAction(() => (this._groups[`${withUsers}`] = groups));
    return groups;
  }

  get tableFilters() {
    const { direction, keyInsideItem } = this.sort;
    return {
      sort: direction,
      sortBy: keyInsideItem,
      orgId: this.orgStore.orgId,
    };
  }

  get sort() {
    const sort = this.urlStore.searchParams?.get("sort");
    const tabSort: TableSort = {
      direction: sort === "desc" || sort === "asc" ? sort : "asc",
      keyInsideItem: this.urlStore.searchParams?.get("sortBy") || "name",
    };
    return tabSort;
  }

  setSort(sort: TableSort) {
    this.urlStore.setSearchParams({
      sort: sort.direction,
      sortBy: sort.keyInsideItem,
    });
  }

  get groups() {
    return fromPromise(this._getGroups(true));
  }

  get groupsList(): IPromiseBasedObservable<Omit<UserGroup, "users">[]> {
    return fromPromise(this._getGroups(false));
  }

  async createGroup(navigate: NavigateFunction) {
    if (!this.canUpdateGroup) return;
    const data = await modalService.openModal(AddGroupModal).promise;
    if (!data) {
      return;
    }
    await this.pendingOperation;
    const { name, permissions, personAutoSyncStatus, groupAutoSyncStatus } = data;
    const orgId = await this.orgStore.orgId;
    const request = postJSON({
      urlSuffix: `/integrations/user-groups/${orgId}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({ name, permissions: permissions.toString(), personAutoSyncStatus, groupAutoSyncStatus }),
    }).then((r) => parseOrReturnError<CreateGroupResponse>(r, this.logger));
    this.pendingOperation = request;
    const res = await request;
    if (res.success && res.group) {
      runInAction(() => {
        this._groups.true?.push(res.group as any);
        this._groups.false?.push(res.group);
        navigate(`/admin/groups/${res.group._id}?orgId=${orgId}`);
      });
    }
    if (!res.success && res.error) {
      modalService.openModal(Toast, {
        text: res.error === MONGO_ERRORS.DUPLICATE_KEY ? i18n.t("user_groups.errors.duplicate_key") : i18n.t("user_groups.errors.default"),
        type: "error",
      });
    }
  }

  async updateGroup(group: UserGroup, newGroupData: { name: string; permissions: string; configs: UserGroupConfigType }) {
    if (!this.canUpdateGroup) return;
    const { name, permissions, configs } = newGroupData;
    const { name: oldName, permissions: oldPermissions } = group;
    const oldConfigs = { ...group.configs };
    runInAction(() => {
      group.name = name;
      group.permissions = permissions;
      group.configs = { ...group.configs, ...configs };
    });

    const orgId = await this.orgStore.orgId;
    const request = putJSON({
      urlSuffix: `/integrations/user-groups/${orgId}/${group._id}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({
        name,
        permissions,
        ...configs,
      }),
    }).then((r) => parseOrReturnError<AssignUsersToGroupResponse>(r, this.logger));
    const res = await request;
    runInAction(() => {
      if (!res.success) {
        group.name = oldName;
        group.permissions = oldPermissions;
        group.configs = oldConfigs;

        if (res.error === MONGO_ERRORS.DUPLICATE_KEY) {
          modalService.openModal(Toast, {
            text: i18n.t("user_groups.errors.duplicate_key"),
            type: "error",
          });
        } else if (res.error === LICENSE_ERRORS.LIMIT_REACHED) {
          modalService.openModal(Toast, {
            text: i18n.t("user_groups.errors.license_limit_reached"),
            type: "error",
          });
        }
      }
    });
  }

  async removeGroup(group: UserGroup) {
    if (!this.canUpdateGroup) return;
    const confirm = await modalService.openModal(ConfirmModal, {
      title: i18n.t("user_groups.remove_group"),
      body: i18n.t("user_groups.confirm_delete_group"),
    }).promise;
    if (!confirm?.result) {
      return;
    }
    await this.pendingOperation;
    runInAction(() => (group.loading = true));
    const orgId = await this.orgStore.orgId;
    const request = deleteJSON({
      urlSuffix: `/integrations/user-groups/${orgId}/${group._id}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: "{}",
    })
      .then((r) => parseOrReturnError<RemoveGroupResponse>(r, this.logger))
      .catch(() => ({ success: false }));
    this.pendingOperation = request;
    const res = await request;
    runInAction(() => {
      group.loading = false;
      if (res.success) {
        this._groups.true = this._groups.true?.filter((g) => g._id !== group._id);
        this._groups.false = this._groups.false?.filter((g) => g._id !== group._id);
      }
    });
  }

  async addUserToGroup(group: UserGroupWithOptionalUsers, user: GroupMember) {
    if (!this.casAssignGroup) return { success: false };
    await this.pendingOperation;
    runInAction(() => {
      group.loading = true;
      group.users?.push(user);
    });
    const orgId = await this.orgStore.orgId;
    const request = postJSON({
      urlSuffix: `/integrations/user-groups/${orgId}/${group._id}/members`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({ users: [user._id] }),
    })
      .then((r) => parseOrReturnError<AssignUsersToGroupResponse>(r, this.logger))
      .catch(() => ({ success: false }));
    this.pendingOperation = request;
    const res = await request;
    runInAction(() => {
      const usr = group.users?.find((u) => u._id === user._id);
      if (usr) usr.loading = false;
      group.loading = false;
      if (!res.success) {
        group.users = group.users?.filter((u) => u._id !== user._id);
        if ("error" in res && res.error === LICENSE_ERRORS.LIMIT_REACHED) {
          modalService.openModal(Toast, {
            text: i18n.t("user_groups.errors.license_limit_reached"),
            type: "error",
          });
        }
      }
    });
    return res;
  }
  async removeUserFromGroup(group: UserGroupWithOptionalUsers, user: GroupMember) {
    if (!this.casAssignGroup) return { success: false };
    const confirm = await modalService.openModal(ConfirmModal, {
      title: i18n.t("user_groups.remove_user", { userName: "" }),
      body: i18n.t("user_groups.confirm_remove_user"),
    }).promise;
    if (!confirm?.result) {
      return { success: false };
    }
    await this.pendingOperation;
    runInAction(() => (group.loading = user.loading = true));
    const orgId = await this.orgStore.orgId;
    const request = deleteJSON({
      urlSuffix: `/integrations/user-groups/${orgId}/${group._id}/members`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({ users: [user._id] }),
    })
      .then((r) => parseOrReturnError<AssignUsersToGroupResponse>(r, this.logger))
      .catch(() => ({ success: false }));
    this.pendingOperation = request;
    const res = await request;
    runInAction(() => {
      group.loading = user.loading = false;
      if (res.success) {
        group.users = group.users?.filter((u) => u._id !== user._id);
      }
    });
    return res;
  }

  get showDefaultAutoSyncConfig() {
    return this.orgConfigsStore.getObservableConfig("show_default_auto_sync_config");
  }

  get channelConfigs() {
    return this.orgConfigsStore.getObservableConfigs([
      "allow_telegram",
      "allow_imessage",
      "allow_mobile_call_recording",
      "enable_whatsapp_voice_video",
    ] as const);
  }

  getGroup(groupId: string) {
    return fromPromise(this.groups.then((groups) => groups.find((group) => group._id === groupId)));
  }
}
